import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useTranslation } from "i18n-fe";
import { TableSkeleton, useActiveOrg, userDisplayName } from "common-fe";
import { useScoreApi, unwrap } from "../api/scoreApi";
import useOrgMembersById from "../hooks/useOrgMembersById";
import useVenueDateTime from "../hooks/useVenueDateTime";
import RideLink from "./RideLink";

// #3480 - the Review Queue's Done tab: what was already resolved, who chose
// what and when, and an Undo while the score is still ours to take back. The
// server decides whether Undo is on offer (item.undo) and says why not in plain
// words; this only draws it. Undo changes a score for the sheet-version kind, so
// every Undo asks first.
// #3534 - a test-change Switch can be put back; a Keep changed nothing, so it
// never shows Undo (the server withholds it too, this is the same rule twice).
const canUndo = (item) =>
  item.undo?.available === true && (item.kind !== "fvd_test_change" || item.action === "switch");

// #3563 - these reasons (score-common error-messages.js) end "use the Ride Editor", so the
// row gets a link to that ride there. The server sends the reason's code with it.
const RIDE_EDITOR_REASON_CODES = new Set(["undo_locked", "undo_not_supported", "undo_changed_since"]);
const pointsAtRideEditor = (item, code) => !!item?.rideId && RIDE_EDITOR_REASON_CODES.has(code);

export default function ReviewDoneTab({ showId, ready = true, onUndone }) {
  const { t } = useTranslation("scoreadmin");
  const scoreApi = useScoreApi();
  const formatWhen = useVenueDateTime();
  const { activeOrgId } = useActiveOrg();
  const membersById = useOrgMembersById(activeOrgId);
  const [state, setState] = useState({ items: [], loading: true, error: false });
  const [confirmItem, setConfirmItem] = useState(null);
  const [undoing, setUndoing] = useState(false);
  const [undoError, setUndoError] = useState(null); // { message, item, code }
  const [notice, setNotice] = useState(false);

  const refetch = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: false }));
    try {
      const items = unwrap(await scoreApi.reviewQueue.listReviewDone(showId || undefined));
      setState({ items, loading: false, error: false });
    } catch {
      setState((s) => ({ ...s, loading: false, error: true }));
    }
  }, [scoreApi, showId]);

  useEffect(() => {
    if (ready) refetch();
  }, [ready, refetch]);

  const who = (sub) => (sub ? userDisplayName(membersById[sub]) || t("reviewQueue.done.unknownPerson") : t("reviewQueue.done.unknownPerson"));

  const doUndo = async () => {
    const item = confirmItem;
    setUndoing(true);
    setUndoError(null);
    try {
      unwrap(await scoreApi.reviewQueue.undoReviewItem({ eventSheetId: item.eventSheetId, eventSeq: item.eventSeq }));
      setConfirmItem(null);
      setNotice(true);
      await refetch();
      if (onUndone) await onUndone();
    } catch (err) {
      // The 409 bodies are written in show-office words (score-common
      // error-messages.js), so they can be shown as they are.
      setUndoError({
        message: err?.response?.data?.data?.message || t("reviewQueue.done.undoError"),
        item,
        code: err?.response?.data?.code ?? null,
      });
      setConfirmItem(null);
      await refetch();
    } finally {
      setUndoing(false);
    }
  };

  const { items, loading, error } = state;
  return (
    <Box data-testid="review-done-tab">
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        {t("reviewQueue.done.subtitle")}
      </Typography>
      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} data-testid="review-done-undone-notice">
          {t("reviewQueue.done.undone")}
        </Alert>
      )}
      {undoError && (
        <Alert severity="error" sx={{ mb: 2 }} data-testid="review-done-undo-error">
          {undoError.message}
          {pointsAtRideEditor(undoError.item, undoError.code) && (
            <>
              {" "}
              <RideLink rideId={undoError.item.rideId} sheetId={undoError.item.sheetId} data-testid="review-done-undo-error-ride-link">
                {t("reviewQueue.done.openInRideEditor")}
              </RideLink>
            </>
          )}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t("reviewQueue.done.loadError")}
        </Alert>
      )}
      {loading && items.length === 0 ? (
        <TableSkeleton rows={4} columns={5} />
      ) : items.length === 0 ? (
        <Typography color="text.secondary" data-testid="review-done-empty">
          {t("reviewQueue.done.empty")}
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small" data-testid="review-done-table">
            <TableHead>
              <TableRow>
                <TableCell>{t("reviewQueue.done.colChoice")}</TableCell>
                <TableCell>{t("reviewQueue.done.colRide")}</TableCell>
                <TableCell>{t("reviewQueue.done.colBy")}</TableCell>
                <TableCell>{t("reviewQueue.done.colWhen")}</TableCell>
                <TableCell>{t("reviewQueue.done.colUndo")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id} data-testid="review-done-row">
                  <TableCell>
                    <Typography variant="body2" color="text.primary">
                      {item.title}
                    </Typography>
                    {item.note && (
                      <Typography variant="caption" color="text.secondary">
                        {item.note}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.primary">
                      {item.subject || "-"}
                    </Typography>
                    {item.judgeName && (
                      <Typography variant="caption" color="text.secondary">
                        {t("reviewQueue.done.judge", { judge: item.judgeName })}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell data-testid="review-done-by">{who(item.resolvedBy)}</TableCell>
                  <TableCell>{formatWhen(item.resolvedAt) ?? "-"}</TableCell>
                  <TableCell>
                    {canUndo(item) ? (
                      <Button size="small" variant="outlined" onClick={() => setConfirmItem(item)} data-testid="review-done-undo">
                        {t("reviewQueue.done.undoButton")}
                      </Button>
                    ) : (
                      <>
                        <Typography variant="body2" color="text.secondary" data-testid="review-done-undo-reason">
                          {item.undo?.reason}
                        </Typography>
                        {pointsAtRideEditor(item, item.undo?.code) && (
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            <RideLink rideId={item.rideId} sheetId={item.sheetId} data-testid="review-done-ride-link">
                              {t("reviewQueue.done.openInRideEditor")}
                            </RideLink>
                          </Typography>
                        )}
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={!!confirmItem} onClose={undoing ? undefined : () => setConfirmItem(null)} maxWidth="sm" fullWidth data-testid="review-done-undo-confirm">
        <DialogTitle>{t("reviewQueue.done.confirmTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText color="text.primary">
            {confirmItem?.kind === "sheet_pick"
              ? t("reviewQueue.done.confirmSheet")
              : confirmItem?.kind === "manual_ride_attach"
                ? t("reviewQueue.done.confirmAttach")
                : confirmItem?.kind === "orphan_sheet"
                  ? t("reviewQueue.done.confirmOrphanDiscard")
                  : confirmItem?.kind === "fvd_test_change"
                    ? t("reviewQueue.done.confirmFvdTestChange")
                    : t("reviewQueue.done.confirmFlag")}
          </DialogContentText>
          {confirmItem?.subject && (
            <Typography sx={{ mt: 1 }} color="text.secondary">
              {confirmItem.subject}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmItem(null)} disabled={undoing} sx={{ color: "text.primary" }} data-testid="review-done-undo-cancel">
            {t("reviewQueue.done.confirmCancel")}
          </Button>
          <Button variant="contained" onClick={doUndo} disabled={undoing} data-testid="review-done-undo-confirm-button">
            {t("reviewQueue.done.confirmButton")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
